import { z } from 'zod'

import { ApiError } from '@/shared/api/client'
import { auditShape } from '@/shared/details'
import type { BadgeTone } from '@/shared/ui'

export * from './paths'

/** An enrollment's statuses, as the API names them. */
export const enrollmentStatuses = ['requested', 'waitlisted', 'enrolled', 'denied', 'canceled'] as const

export type EnrollmentStatus = (typeof enrollmentStatuses)[number]

/** The statuses a new enrollment may start in. */
export const createStatuses = ['enrolled', 'requested', 'waitlisted'] as const

/** How a person attends, as the API names it. */
export const attendanceModes = ['inPerson', 'virtual', 'toBeDecided'] as const

export type AttendanceMode = (typeof attendanceModes)[number]

/** How each status shows. */
export const statusTones: Readonly<Record<EnrollmentStatus, BadgeTone>> = {
  requested: 'neutral',
  waitlisted: 'primary',
  enrolled: 'success',
  denied: 'error',
  canceled: 'error',
}

/** The moves the status endpoint takes. */
export const statusActions = ['approve', 'deny', 'waitlist', 'enroll', 'cancel', 'reinstate'] as const

export type StatusAction = (typeof statusActions)[number]

/** The statuses each move starts from, as the API allows them. */
const movesFrom: Readonly<Record<StatusAction, readonly EnrollmentStatus[]>> = {
  approve: ['requested'],
  deny: ['requested', 'waitlisted'],
  waitlist: ['requested', 'enrolled', 'denied', 'canceled'],
  // From a request it is Approve.
  enroll: ['waitlisted', 'denied', 'canceled'],
  cancel: ['requested', 'waitlisted', 'enrolled'],
  reinstate: ['denied', 'canceled'],
}

/** The moves an enrollment in this status may make, in the order the menu shows them. */
export function movesOf(status: EnrollmentStatus): readonly StatusAction[] {
  return statusActions.filter((action) => movesFrom[action].includes(status))
}

const enrollmentSchema = z
  .object({
    id: z.string(),
    classId: z.string(),
    className: z.string().nullish(),
    courseId: z.string(),
    userId: z.string(),
    userName: z.string(),
    organizationId: z.string(),
    learnerOrganizationId: z.string(),
    status: z.enum(enrollmentStatuses),
    classroomId: z.string().nullish(),
    attendance: z.enum(attendanceModes),
    lateCancel: z.boolean(),
    retake: z.boolean(),
    labsEnabled: z.boolean(),
    bypassPrerequisites: z.boolean(),
    postClassLabDays: z.number().int().nullish(),
    labsAvailableUntil: z.string().nullish(),
    classStartsOn: z.string().nullish(),
    paidBySubscriptionId: z.string().nullish(),
    paidByTrainingKeyId: z.string().nullish(),
    externalId: z.string().nullish(),
    price: z.number().nullish(),
  })
  .extend(auditShape)

/** An enrollment as Enrollment Details and its edit form show it. */
export type Enrollment = z.infer<typeof enrollmentSchema>

export function parseEnrollment(body: unknown): Enrollment {
  return enrollmentSchema.parse(body)
}

/**
 * Whether the enrollment may still be deleted, as My Training offers a cancel: requested,
 * waitlisted or enrolled, in a class known not to have started yet.
 */
export function isDeletable(enrollment: Enrollment, now: number = Date.now()): boolean {
  const { status, classStartsOn } = enrollment
  return (status === 'requested' || status === 'waitlisted' || status === 'enrolled') && classStartsOn !== null && classStartsOn !== undefined && now < Date.parse(classStartsOn)
}

const classFactsSchema = z.object({
  id: z.string(),
  name: z.string(),
  courseId: z.string(),
  deliveryType: z.string(),
  classrooms: z.array(z.object({ id: z.string(), name: z.string() })),
})

/** What the enrollment screens read of a class: its name, course and classrooms. */
export type ClassFacts = z.infer<typeof classFactsSchema>

export function parseClassFacts(body: unknown): ClassFacts {
  return classFactsSchema.parse(body)
}

/** The API's code for an enrollment that clashes with other classes, which the caller may confirm past. */
export const conflictsCode = 'enrollment.conflicts'

/** The clashes a 409 lists, by kind (such as `classroom` or `schedule`), each with what it clashes with; `null` for any other answer. */
export function conflictsOf(error: unknown): ReadonlyMap<string, readonly string[]> | null {
  if (!(error instanceof ApiError) || error.code !== conflictsCode) {
    return null
  }
  return new Map(Object.entries(error.fieldErrors))
}

/** What a learner is told a clash 409 means, from the kinds it lists: a full classroom, another class of theirs at the same time, or both. */
export function clashReasonOf(error: ApiError): 'roomFull' | 'scheduleClash' | 'roomFullAndScheduleClash' {
  const isRoomFull = (error.fieldErrors['classroom']?.length ?? 0) > 0
  const isScheduleClash = (error.fieldErrors['schedule']?.length ?? 0) > 0
  if (!isRoomFull) {
    return 'scheduleClash'
  }
  return isScheduleClash ? 'roomFullAndScheduleClash' : 'roomFull'
}

/** Explains the refusals an enrollment's change can meet, by the API's code. */
export const refusalIds: Readonly<Record<string, string>> = {
  'enrollment.class_full': 'classFull',
  'enrollment.instructor_of_class': 'instructor',
  'enrollment.prerequisites_not_met': 'prerequisites',
  'enrollment.class_not_open': 'classNotOpen',
  'enrollment.course_not_delivered': 'courseNotDelivered',
  'enrollment.subscription_not_active': 'payerNotActive',
  'enrollment.status_not_allowed': 'statusNotAllowed',
  'enrollment.outside_class_window': 'outsideWindow',
  'enrollment.class_started': 'classStarted',
  'enrollment.class_gone': 'classGone',
  'enrollment.action_forbidden': 'actionForbidden',
  'enrollment.seats_busy': 'seatsBusy',
  'enrollment.already_in_class': 'alreadyInClass',
  'enrollment.learner_inactive': 'learnerInactive',
}
